// src/pages/AddTerritoryPage.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Paper,
  Divider,
  Stack,
  Autocomplete,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Modal,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import API from "../utils/api";
import { toast } from "react-toastify";
import SmartTable from "../components/Layout/SmartTable";
import { alpha } from "@mui/material/styles";
import LeadStatusCell from "../components/LeadStatusCell";
import { LeadStatusSelectMenuItems } from "../components/LeadStatusMenuItems";




export default function AddTerritoryPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    manager: "",
    assigned_reps: [],
  });

  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  
  // Lead functionality state
  const [tab, setTab] = useState(0);
  const [file, setFile] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    status: "",
    assigned_rep: "",
  });
  const [leadFormData, setLeadFormData] = useState({
    first_name: "",
    last_name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    status: "New",
    assigned_rep: "",
    latitude: "",
    longitude: "",
  });

  useEffect(() => {
    fetchUsers();
    // Don't fetch leads initially - this is a new territory creation page
    setLeads([]);
  }, []);

  // 🔧 FIX: do NOT auto-fetch leads while typing a territory name.
  // Only clear when name is empty; actual fetches occur on explicit actions.
  useEffect(() => {
    if (!formData.name) {
      setLeads([]);
    }
  }, [formData.name]);

  const fetchUsers = async () => {
    try {
      const res = await API.get("/users/");
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("❌ Failed to load managers");
    }
  };

  const fetchLeads = async () => {
    try {
      const res = await API.get("/leads/");
      setLeads(res.data);
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("❌ Failed to load leads");
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.manager) {
      toast.error("⚠️ Please fill all required fields");
      return;
    }

    try {
      await API.post("/territories/", formData);
      toast.success("✅ Territory added successfully!");
      navigate("/territories");
    } catch (err) {
      console.error("Error saving territory:", err.response?.data || err.message);
      toast.error("❌ Failed to save territory");
    }
  };

  const handleCancel = () => navigate("/territories");

  // Lead functionality functions
  const handleFileUpload = async () => {
    if (!file) return toast.error("Please select a file first.");
    if (!formData.name) return toast.error("Please enter a territory name first.");
    
    const data = new FormData();
    data.append("file", file);
    data.append("territory_name", formData.name);

    try {
    await API.post("/import-leads/", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
      toast.success("✅ Leads uploaded successfully!");
      await refreshLeads();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("❌ Upload failed");
    }
  };

  const handleSaveLead = async () => {
    if (!formData.name) {
      toast.warning("⚠️ Please enter a territory name first.");
      return;
    }
    if (!leadFormData.latitude || !leadFormData.longitude) {
      toast.warning("⚠️ Please provide both Latitude and Longitude before saving.");
      return;
    }

    try {
      const payload = {
        first_name: leadFormData.first_name,
        last_name: leadFormData.last_name,
        address: leadFormData.address,
        city: leadFormData.city,
        state: leadFormData.state,
        zip_code: leadFormData.zip_code,
        status: leadFormData.status,
        assigned_rep: leadFormData.assigned_rep || null,
        latitude: parseFloat(leadFormData.latitude),
        longitude: parseFloat(leadFormData.longitude),
        territory: formData.name,
      };

      await API.post("/leads/", payload);
      toast.success("✅ Lead added successfully!");
      await refreshLeads();
      setLeadFormData({
        first_name: "",
        last_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        status: "New",
        assigned_rep: "",
        latitude: "",
        longitude: "",
      });
    } catch (err) {
      console.error("Error saving lead:", err);
      toast.error("❌ Failed to add lead");
    }
  };

  const refreshLeads = async () => {
    try {
      // Only fetch leads if we have a territory name to filter by
      if (formData.name) {
        const res = await API.get(`/leads/?territory_name=${encodeURIComponent(formData.name)}`);
        setLeads(res.data);
      } else {
        // If no territory name, show empty leads
        setLeads([]);
      }
    } catch (err) {
      console.error("Error refreshing leads:", err);
      setLeads([]);
    }
  };

  const handleRowClick = (params) => {
    const row = params?.row;
    if (!row) return;

    setSelectedLead(row);
    setEditForm({
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      status: row.status || "New",
      assigned_rep: row.assigned_rep || "",
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedLead(null);
  };

  const handleEditSave = async () => {
    if (!selectedLead) return;

    try {
      const payload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        status: editForm.status,
        assigned_rep: editForm.assigned_rep || null,
      };

      await API.put(`/leads/${selectedLead.id}/`, payload);
      toast.success("✅ Lead updated successfully!");
      await refreshLeads();
      handleEditClose();
    } catch (err) {
      console.error("Error updating lead:", err);
      toast.error("❌ Failed to update lead");
    }
  };

  const leadColumns = [
    { field: "first_name", headerName: "First Name", flex: 1 },
    { field: "last_name", headerName: "Last Name", flex: 1 },
    { field: "address", headerName: "Address", flex: 1.5 },
    { field: "city", headerName: "City", flex: 1 },
    { field: "state", headerName: "State", flex: 0.8 },
    { field: "zip_code", headerName: "ZIP", flex: 0.8 },
    {
  field: "status",
  headerName: "Status",
  flex: 1.3,
  renderCell: (params) => {
    const value = params?.row?.status || "New";
    return <LeadStatusCell status={value} />;
  },
},

    {
      field: "territory_name",
      headerName: "Territory",
      flex: 1,
      valueGetter: (params) => params?.row?.territory_name || "—",
    },
    {
      field: "assigned_rep_name",
      headerName: "Assigned Rep",
      flex: 1.2,
      valueGetter: (params) => params?.row?.assigned_rep_name || "—",
    },
    { field: "latitude", headerName: "Lat", flex: 0.6 },
    { field: "longitude", headerName: "Lng", flex: 0.6 },

    {
      field: 'notes',
      headerName: 'Notes',
      flex: 2,
      renderCell: (params) => {
        if (!params) return "—";
        return <span title={params.value}>{params.value || '—'}</span>;
      },
    },
    {
      field: "sold_date",
      headerName: "Sold",
      flex: 0.8,
      valueGetter: (params) => {
        if (!params?.row) return "—";
        return params.row.sold_date
          ? new Date(params.row.sold_date).toLocaleDateString()
          : "—";
      },
    },
    {
      field: "property_type",
      headerName: "Type",
      flex: 1,
      valueGetter: (p) => {
        if (!p?.row) return "—";
        return p.row.property_type || "—";
      },
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      valueFormatter: (params) => {
        if (!params || params.value == null) return "—";
        return `$${Number(params.value).toLocaleString()}`;
      },
    },
    {
      field: "square_feet",
      headerName: "SqFt",
      flex: 0.8,
    },
    {
      field: "year_built",
      headerName: "Year",
      flex: 0.7,
    },

    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.created_at
          ? new Date(params.row.created_at).toLocaleDateString()
          : "—",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 5,
        minHeight: "100vh",
        p: 3,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      {/* ====================== CONTAINER 1 ====================== */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "600px",
          maxWidth: "100%",
          borderRadius: "16px",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          mt: 4,
        }}
      >
        {/* ---------------------------------------------------Unified Header-------------------- */}
<Box
  sx={{
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "12px 12px 0 0",
    px: 3,
    py: 2.5,
    mb: 3,
  }}
>
  <Typography
    variant="h6"
    sx={{
      color: "primary.main",
      fontWeight: 700,
      letterSpacing: 0.3,
    }}
  >
    Add New Territory
  </Typography>
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ mt: 0.5 }}
  >
    Create a new territory, assign a manager, reps and track lead counts.
  </Typography>
</Box>


        

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Territory Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            variant="outlined"
          />

          <TextField
            select
            fullWidth
            label="Manager"
            value={formData.manager}
            onChange={(e) => handleChange("manager", e.target.value)}
            variant="outlined"
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.username}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            multiple
            options={users}
            getOptionLabel={(option) => option.username}
            value={users.filter((u) => formData.assigned_reps.includes(u.id))}
            onChange={(e, value) =>
              handleChange(
                "assigned_reps",
                value.map((v) => v.id)
              )
            }
            renderInput={(params) => (
              <TextField {...params} label="Assigned Reps" variant="outlined" />
            )}
          />

          <TextField
            type="number"
            fullWidth
            label="Lead Count"
            value={formData.lead_count}
            onChange={(e) => handleChange("lead_count", e.target.value)}
            variant="outlined"
            inputProps={{ min: 0 }}
          />
        </Stack>

        <Box
          display="flex"
          justifyContent="space-between"
          gap={2}
          mt={4}
          pt={2}
          borderTop={`1px solid ${theme.palette.divider}`}
          sx={{ backgroundColor: "inherit" }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            sx={{ width: "48%" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ width: "48%" }}
          >
            Save Territory
          </Button>
        </Box>
      </Paper>

      {/* ====================== CONTAINER 2 — LEADS TABLE ====================== */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: "1200px",
          borderRadius: "16px",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          mb: 6,
        }}
      >
        {/* ------------------------------Unified Header------------------------ */}
<Box
  sx={{
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "12px 12px 0 0",
    px: 3,
    py: 2.5,
    mb: 3,
  }}
>
  <Typography
    variant="h6"
    sx={{
      color: "primary.main",
      fontWeight: 700,
      letterSpacing: 0.3,
    }}
  >
    Leads in This Territory
  </Typography>
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ mt: 0.5 }}
  >
    View and manage all leads associated with this territory.
  </Typography>
</Box>


        <Divider sx={{ mb: 3, borderColor: theme.palette.divider }} />

        {!formData.name ? (
          <Typography
            variant="body2"
            color="text.secondary"
            mb={3}
            sx={{ textAlign: "center" }}
          >
            Enter a territory name above to start adding leads.
          </Typography>
        ) : leads.length > 0 ? (
          <Box mb={4}>
            <SmartTable
              rows={leads}
              columns={leadColumns}
              onRowClick={handleRowClick}
	mobileEssentialFields={[
  "first_name",
  "last_name",
  "address",
  "city",
  "state",
  "status",
  "assigned_rep_name",
]}

              key={JSON.stringify(leads.map((l) => `${l.id}-${l.first_name}`))}
            />
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            mb={3}
            sx={{ textAlign: "center" }}
          >
            No leads currently assigned to this territory.
          </Typography>
        )}

        <Divider sx={{ mb: 3 }} />

      <Tabs
  value={tab}
  onChange={(_, newVal) => setTab(newVal)}
  variant="fullWidth"
  TabIndicatorProps={{ style: { display: "none" } }}   // 🔹 Disable built-in indicator
  sx={{
    mb: 3,

    "& .MuiTabs-flexContainer": {
      flexDirection: { xs: "column", md: "row" },
    },
  }}
>
  <Tab
    icon={<DriveFolderUploadIcon />}
    label="Upload Leads from File"
    sx={{
      fontSize: { xs: "0.75rem", md: "0.875rem" },
      width: { xs: "100%", md: "auto" },
      minWidth: { xs: "100%", md: 160 },

      // 🔹 Selected tab blue bar
      borderBottom: {
        xs: tab === 0 ? "3px solid #1976d2" : "3px solid transparent",
        md: "none",
      },
    }}
  />

  <Tab
    icon={<PersonAddIcon />}
    label="Add Individual Lead"
    sx={{
      fontSize: { xs: "0.75rem", md: "0.875rem" },
      width: { xs: "100%", md: "auto" },
      minWidth: { xs: "100%", md: 160 },

      borderBottom: {
        xs: tab === 1 ? "3px solid #1976d2" : "3px solid transparent",
        md: "none",
      },
    }}
  />
</Tabs>







        {tab === 0 && (
          <Box textAlign="center">
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent={{ xs: "flex-start", md: "center" }}
              sx={{ mb: 3 }}
            >
              <Button 
                variant="contained" 
                component="label"
                size="small"
                sx={{ 
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  width: { xs: "100%", md: "auto" }
                }}
              >
                Choose File
                <input
                  type="file"
                  accept=".csv, .xlsx"
                  hidden
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </Button>
              {file && (
                <Typography 
                  sx={{ 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    textAlign: { xs: "center", md: "left" },
                    wordBreak: "break-word",
                  }}
                >
                  {file.name}
                </Typography>
              )}
            </Stack>
            <Button
              variant="outlined"
              color="success"
              startIcon={<DriveFolderUploadIcon />}
              onClick={handleFileUpload}
              disabled={!file}
              size="small"
              sx={{ 
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                width: { xs: "100%", md: "auto" },
                borderRadius: 1.5,
              }}
            >
              Upload File
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="First Name"
                  name="first_name"
                  fullWidth
                  value={leadFormData.first_name}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, first_name: e.target.value })
                  }
                />
                <TextField
                  label="Last Name"
                  name="last_name"
                  fullWidth
                  value={leadFormData.last_name}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, last_name: e.target.value })
                  }
                />
              </Stack>

              <TextField
                label="Address"
                name="address"
                fullWidth
                value={leadFormData.address || ""}
                onChange={(e) =>
                  setLeadFormData({ ...leadFormData, address: e.target.value })
                }
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="City"
                  name="city"
                  fullWidth
                  value={leadFormData.city || ""}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, city: e.target.value })
                  }
                />
                <TextField
                  label="State"
                  name="state"
                  fullWidth
                  value={leadFormData.state || ""}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, state: e.target.value })
                  }
                />
                <TextField
                  label="ZIP Code"
                  name="zip_code"
                  fullWidth
                  value={leadFormData.zip_code || ""}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, zip_code: e.target.value })
                  }
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={leadFormData.status}
                    label="Status"
                    onChange={(e) =>
                      setLeadFormData({ ...leadFormData, status: e.target.value })
                    }
                  >
                    <LeadStatusSelectMenuItems />
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Latitude (REQUIRED)"
                  name="latitude"
                  fullWidth
                  required
                  value={leadFormData.latitude}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, latitude: e.target.value })
                  }
                />
                <TextField
                  label="Longitude (REQUIRED)"
                  name="longitude"
                  fullWidth
                  required
                  value={leadFormData.longitude}
                  onChange={(e) =>
                    setLeadFormData({ ...leadFormData, longitude: e.target.value })
                  }
                />
              </Stack>

              <Stack 
                direction={{ xs: "column", md: "row" }} 
                spacing={2} 
                justifyContent={{ xs: "stretch", md: "flex-end" }}
                sx={{ width: "100%" }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setLeadFormData({
                    first_name: "",
                    last_name: "",
                    address: "",
                    city: "",
                    state: "",
                    zip_code: "",
                    status: "New",
                    assigned_rep: "",
                    latitude: "",
                    longitude: "",
                  })}
                  size="small"
                  sx={{ 
                    textTransform: "none", 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    width: { xs: "100%", md: "auto" }
                  }}
                >
                  Clear Form
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveLead}
                  size="small"
                  sx={{ 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    width: { xs: "100%", md: "auto" }
                  }}
                >
                  Save Lead
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Edit Lead Modal */}
        <Modal open={editOpen} onClose={handleEditClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: theme.palette.background.paper,
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              width: 400,
            }}
          >
            <Typography variant="h6" mb={2}>
              Edit Lead
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="First Name"
                fullWidth
                value={editForm.first_name}
                onChange={(e) =>
                  setEditForm({ ...editForm, first_name: e.target.value })
                }
              />
              <TextField
                label="Last Name"
                fullWidth
                value={editForm.last_name}
                onChange={(e) =>
                  setEditForm({ ...editForm, last_name: e.target.value })
                }
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  label="Status"
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm({ ...editForm, status: e.target.value })
                  }
                >
                  <LeadStatusSelectMenuItems />
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleEditClose}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleEditSave}>
                  Save Changes
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>
      </Paper>
    </Box>
  );
}
